import { cleanPermalink } from '../../statistics/lib/top-videos';
import { format } from 'date-fns';

import { projectId } from '../../../utils/supabase/info';
import { getAuthHeaders } from '../../statistics/api/auth-headers';
import {
  applyBatchUpdate,
  createDoc,
  DOCS_SCOPE,
  renderLines,
  type LineSpec,
} from '../../statistics/lib/export-docs';
import { getAccessToken } from '../../statistics/lib/google-auth';
import { exactRu } from '../../statistics/lib/format-metrics-ru';
import type { SeedingGroup } from './group-seeding';
import { drawCards, formatPeriod, type SeedingDocParams } from './export-seeding-docx';
import type { CommentCardImage } from './render-comment-card';
import { COVER_WIDTH, renderCovers } from './render-cover';

/**
 * «Отработано» новым Google-документом, а не скачанным файлом.
 *
 * Просьба Артура: «хочу тут чтобы не word скачивать каждый раз, а просто
 * открывался в гугл документе новым файлом». Связка для этого в проекте уже
 * работает — ей собирается «Отчет по выполненным роликам»
 * (statistics/lib/export-docs): вход через Google Identity, createDoc,
 * renderLines с форматированием и window.open на готовый документ. Здесь те же
 * кирпичи, только строки другие.
 *
 * Отступ ссылок и цифр — два дюйма, как в образце (в Word это 2880 твипов,
 * здесь 144 PT: одно и то же расстояние, разные единицы).
 *
 * Картинки — обложки и комментарии — приходится заливать в хранилище:
 * insertInlineImage принимает только публичный адрес, data: он не берёт.
 * Заливка та же, что у скриншотов отчёта по роликам.
 */

const UPLOAD_URL = `https://${projectId}.supabase.co/functions/v1/make-server-bde4f709/instagram/upload-image`;

/** Два дюйма в PT — тот же отступ, что 2880 твипов в Word. */
const INDENT_PT = 144;
/** 340 точек карточки при 96 dpi — в PT для Docs. */
const CARD_WIDTH_PT = 255;
/** Обложка: 240 точек при 96 dpi — 180 PT. */
const COVER_WIDTH_PT = (COVER_WIDTH * 72) / 96;

const HEADING = 'ОТРАБОТАНО Instagram ✅';
const COMMENTS_HEADING = 'Топовые комментарии под роликом:';

/** Картинка на своё место в документе: карточка комментария или обложка. */
type ImageSlot = { line: number; key: string; kind: 'card' | 'cover'; fallback?: string };

async function uploadImage(image: CommentCardImage): Promise<string | null> {
  const contentType = image.dataUrl.slice(5, image.dataUrl.indexOf(';')) || 'image/png';
  try {
    const headers = await getAuthHeaders();
    const res = await fetch(UPLOAD_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ imageBase64: image.dataUrl, contentType }),
    });
    if (!res.ok) {
      console.warn('[Отработано] картинка не залилась:', res.status);
      return null;
    }
    const { publicUrl } = (await res.json()) as { publicUrl?: string };
    return publicUrl ?? null;
  } catch (err) {
    console.warn('[Отработано] картинка не залилась:', err);
    return null;
  }
}

/** Строки документа плюс места, куда встанут обложки и картинки комментариев. */
function buildLines(params: SeedingDocParams): { lines: LineSpec[]; slots: ImageSlot[] } {
  const lines: LineSpec[] = [{ text: HEADING }];
  const slots: ImageSlot[] = [];
  if (params.period) lines.push({ text: formatPeriod(params.period) });
  lines.push({ text: '' }, { text: `Топ ${params.groups.length} видео:` }, { text: '' });

  const metrics = (group: SeedingGroup): Array<[string, number]> => {
    const rows: Array<[string, number]> = [
      ['Просмотры', group.totals.views],
      ['Лайки', group.totals.likes],
      ['Комментарии', group.totals.comments],
    ];
    // Репостов может не быть вовсе — «Репосты - 0» читалось бы как провал.
    if (group.totals.reposts !== null) rows.push(['Репосты', group.totals.reposts]);
    return rows;
  };

  for (const group of params.groups) {
    lines.push({ text: group.headline, highlight: group.headline !== '' });
    for (const item of group.items) {
      lines.push({ text: cleanPermalink(item.url), linkUrl: cleanPermalink(item.url), tabIndentPt: INDENT_PT });
    }
    // Место под обложку — только если она нарисовалась: пустая строка вместо
    // кадра выглядела бы дырой в блоке.
    if (group.cover && params.covers?.has(group.cover)) {
      slots.push({ line: lines.length, key: group.cover, kind: 'cover' });
      lines.push({ text: '' });
    }
    if (group.comments && group.comments.length > 0) {
      lines.push({ text: '' }, { text: COMMENTS_HEADING, bold: true });
      for (const comment of group.comments) {
        slots.push({
          line: lines.length, key: comment.id, kind: 'card',
          fallback: `${comment.username}${comment.date ? ` · ${comment.date}` : ''}\n${comment.text}\nОтметки «Нравится»: ${exactRu(comment.likeCount)}`,
        });
        lines.push({ text: '' });
      }
    }
    lines.push({ text: '' });
    for (const [label, value] of metrics(group)) {
      lines.push({ text: `${label} - ${exactRu(value)}`, tabIndentPt: INDENT_PT });
    }
    lines.push({ text: '' });
  }

  return { lines, slots };
}

/**
 * Собирает документ и открывает его в новой вкладке. Ошибки картинок возвращает
 * вызывающему; комментарии при этом сохраняются текстом вместо пустых мест.
 */
export async function generateSeedingGoogleDoc(
  params: SeedingDocParams,
  onProgress: (message: string) => void = () => {},
): Promise<{ url: string; missingImages: number }> {
  if (params.groups.length === 0) throw new Error('Нет данных для экспорта');
  if (!import.meta.env.VITE_GOOGLE_CLIENT_ID) {
    throw new Error('VITE_GOOGLE_CLIENT_ID не задан в .env');
  }

  onProgress('Вход в Google...');
  const token = await getAccessToken(DOCS_SCOPE);
  const docId = await createDoc(
    token,
    `Еженедельный отчет с комментариями ${format(new Date(), 'dd.MM.yyyy')}`,
  );

  const cards = params.cards ?? drawCards(params.groups);
  const covers = params.covers ?? (await renderCovers(params.groups));
  let missingImages = params.groups.filter((g) => g.cover && !covers.has(g.cover)).length;
  const { lines, slots } = buildLines({ ...params, cards, covers });
  const { text, formatRequests, lineStarts } = renderLines(lines, 1);

  onProgress('Текст отчёта...');
  await applyBatchUpdate(token, docId, [
    { insertText: { location: { index: 1 }, text } },
    {
      updateTextStyle: {
        range: { startIndex: 1, endIndex: text.length },
        textStyle: { weightedFontFamily: { fontFamily: 'Times New Roman', weight: 400 } },
        fields: 'weightedFontFamily',
      },
    },
    ...formatRequests,
  ]);

  if (slots.length > 0) {
    onProgress('Картинки...');
    const imageOf = (slot: ImageSlot) => (slot.kind === 'cover' ? covers.get(slot.key) : cards.get(slot.key));
    const urls = new Map<ImageSlot, string>();
    await Promise.all(
      slots.map(async (slot) => {
        const image = imageOf(slot);
        if (!image) return;
        const url = await uploadImage(image);
        if (url) urls.set(slot, url);
      }),
    );

    // С конца: вставка сдвигает всё, что после неё, а то, что до, — нет.
    for (let i = slots.length - 1; i >= 0; i--) {
      const slot = slots[i];
      const url = urls.get(slot);
      const index = lineStarts[slot.line];
      if (index === undefined) throw new Error('Не найдено место для изображения в документе');
      if (url) {
        const widthPt = slot.kind === 'cover' ? COVER_WIDTH_PT : CARD_WIDTH_PT;
        try {
          await applyBatchUpdate(token, docId, [
            {
              insertInlineImage: {
                location: { index },
                uri: url,
                objectSize: { width: { magnitude: widthPt, unit: 'PT' } },
              },
            },
          ]);
          continue;
        } catch (err) {
          console.warn('[Отработано] картинка не вставилась:', err);
        }
      }
      missingImages++;
      if (slot.fallback) {
        await applyBatchUpdate(token, docId, [
          { insertText: { location: { index }, text: slot.fallback } },
        ]);
      }
    }
  }

  const url = `https://docs.google.com/document/d/${docId}/edit`;
  window.open(url, '_blank');
  return { url, missingImages };
}
